import { logError, logEvent } from "@src/appV2/lib/analytics";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryObserverOptions,
} from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { isDevelopmentNodeEnvironment } from "../environment/utils";

export const defaultQueryOptions: QueryObserverOptions = {
  refetchOnWindowFocus: true,
  retry: 1,
  retryDelay: 1000,
  useErrorBoundary: isDevelopmentNodeEnvironment(),
};

// Doesn't work on v4, https://tanstack.com/query/latest/docs/framework/react/typescript#typing-meta
interface Meta {
  logSuccessMessage?: string;
  userSuccessMessage?: string;
  logErrorMessage?: string;
  userErrorMessage?: string;
  toastAutoHideDurationInMilliseconds?: number;
}

export const cbhQueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const { logErrorMessage, userErrorMessage, toastAutoHideDurationInMilliseconds } =
        (query.meta ?? {}) as Meta;

      if (logErrorMessage) {
        logError(String(logErrorMessage), { error });
      }

      if (userErrorMessage) {
        enqueueSnackbar({
          variant: "error",
          message: userErrorMessage,
          autoHideDuration: toastAutoHideDurationInMilliseconds,
        });
      }
    },
    onSuccess: (_, query) => {
      const { logSuccessMessage, userSuccessMessage, toastAutoHideDurationInMilliseconds } =
        (query.meta ?? {}) as Meta;
      if (logSuccessMessage) {
        logEvent(String(logSuccessMessage));
      }

      if (userSuccessMessage) {
        enqueueSnackbar({
          variant: "success",
          message: userSuccessMessage,
          autoHideDuration: toastAutoHideDurationInMilliseconds,
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError(error, _variables, _context, mutation) {
      const { logErrorMessage, userErrorMessage, toastAutoHideDurationInMilliseconds } =
        (mutation.meta ?? {}) as Meta;
      if (logErrorMessage) {
        logError(String(logErrorMessage), { error });
      }

      if (userErrorMessage) {
        enqueueSnackbar({
          variant: "error",
          message: userErrorMessage,
          autoHideDuration: toastAutoHideDurationInMilliseconds,
        });
      }
    },
    onSuccess(_data, _variables, _context, mutation) {
      const { logSuccessMessage, userSuccessMessage, toastAutoHideDurationInMilliseconds } =
        (mutation.meta ?? {}) as Meta;
      if (logSuccessMessage) {
        logEvent(String(logSuccessMessage));
      }

      if (userSuccessMessage) {
        enqueueSnackbar({
          variant: "success",
          message: userSuccessMessage,
          autoHideDuration: toastAutoHideDurationInMilliseconds,
        });
      }
    },
  }),
  defaultOptions: {
    queries: defaultQueryOptions,
  },
});
